// Admin route-tab groups (admin menu simplification, part 2 — 2026-10-05).
//
// Screens that do the same job under one menu entry. Each tab is an existing
// route, untouched; AdminHubTabs (admin-hubs.tsx) draws the strip above the
// page, and the menu shows one entry per group via hubMenuEntry.

import type { adminHubsI18n } from '../../i18n/admin/hubs'

type HubsT = (typeof adminHubsI18n)['en']

export type AdminHubId =
  | 'skuMapping'
  | 'catalogCorrections'
  | 'sellerApplications'
  | 'allFiles'
  | 'catalogPdf'
  | 'stores'

type HubDef = {
  id: AdminHubId
  title: (t: HubsT) => string
  tabs: { to: string; label: (t: HubsT) => string }[]
}

export const ADMIN_HUBS: HubDef[] = [
  // Stores + per-store sales targets: the same stores, two views. Sales Goals
  // needs `sales_goal.view`; AdminShell hides that tab from everyone else.
  {
    id: 'stores',
    title: (t) => t.stores.title,
    tabs: [
      { to: '/admin/stores', label: (t) => t.stores.stores },
      { to: '/admin/sales-goals', label: (t) => t.stores.salesGoals },
      { to: '/admin/scorecard', label: (t) => t.stores.scorecard },
    ],
  },
  {
    id: 'skuMapping',
    title: (t) => t.skuMapping.title,
    tabs: [
      { to: '/admin/listing-triage', label: (t) => t.skuMapping.storeListings },
      { to: '/admin/inventory/unmapped', label: (t) => t.skuMapping.unmappedOnOrders },
      { to: '/admin/catalog/marketplace-listings', label: (t) => t.skuMapping.mappedListings },
      { to: '/admin/catalog/sku-aliases', label: (t) => t.skuMapping.alternateCodes },
    ],
  },
  {
    id: 'catalogCorrections',
    title: (t) => t.catalogCorrections.title,
    tabs: [
      { to: '/admin/pending-updates', label: (t) => t.catalogCorrections.supplierSuggestions },
      { to: '/admin/catalog-issues', label: (t) => t.catalogCorrections.sellerReports },
      { to: '/admin/vendor-replacements', label: (t) => t.catalogCorrections.vendorReplacements },
    ],
  },
  {
    id: 'sellerApplications',
    title: (t) => t.sellerApplications.title,
    tabs: [
      { to: '/admin/leads', label: (t) => t.sellerApplications.leads },
      { to: '/admin/leads/scoreboard', label: (t) => t.sellerApplications.scoreboard },
      { to: '/admin/onboarding', label: (t) => t.sellerApplications.invitations },
      { to: '/admin/self-apply', label: (t) => t.sellerApplications.applications },
    ],
  },
  {
    id: 'allFiles',
    title: (t) => t.allFiles.title,
    tabs: [
      { to: '/admin/drive-explorer', label: (t) => t.allFiles.folderTree },
      { to: '/admin/drive-audit', label: (t) => t.allFiles.fileActivity },
    ],
  },
  {
    id: 'catalogPdf',
    title: (t) => t.catalogPdf.title,
    tabs: [
      { to: '/admin/document-studio/catalogs', label: (t) => t.catalogPdf.newCatalog },
      { to: '/admin/document-studio/files', label: (t) => t.catalogPdf.generatedFiles },
    ],
  },
]

/** The menu entry for a hub: its first tab, lit on every sibling tab too. */
export function hubMenuEntry(id: AdminHubId) {
  const hub = ADMIN_HUBS.find((h) => h.id === id)!
  const [first, ...rest] = hub.tabs
  return { to: first.to, alsoActiveFor: rest.map((tab) => tab.to) }
}
